import { Center, Flex, Stack, Text, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { abs, isNegative, round } from "mathjs";
import { useQueryState } from "nuqs";

import { builder } from "@/builders";
import { useFakeAdminDashboardData } from "@/builders/types/admin-dashboard";
import { pass } from "@/libraries/pass";
import { NewUserSignupIcon } from "@/svgs/new-user-signups-icon";

import { useCurrentORG } from "@/components/super-admin/org-management/use-organisation";
import { ArrowDownIcon } from "@/svgs/arrow-down-icon";
import { ArrowUpIcon } from "@/svgs/arrow-up-icon";
import clsx from "clsx";

export function NewUserSignUps() {
  const initialDashboardData = useFakeAdminDashboardData();
  const { organization } = useCurrentORG();

  const [metric] = useQueryState("metric", {
    defaultValue: "Monthly",
  });

  const { data, isPlaceholderData } = useQuery({
    queryKey: builder.data.admin_dashboard.$get({
      organization: organization.id,
    }),
    queryFn: () =>
      builder.$use.data.admin_dashboard({
        organization: organization.id,
      }),
    select: ({ data }) => data,
    placeholderData: {
      data: initialDashboardData,
    },
  });

  const {
    new_signups_percentage_change_month,
    new_signups_percentage_change_week,
    new_signups_percentage_change_year,
  } = { ...data };

  const view = {
    Monthly: new_signups_percentage_change_month,
    Weekly: new_signups_percentage_change_week,
    Yearly: new_signups_percentage_change_year,
  }[metric];

  return (
    <Stack
      flex={1}
      className="rounded-lg border border-primary-border-light backdrop-blur-[50px]"
      p={16}
      gap={4}
    >
      <Title
        order={2}
        className={clsx("text-white/50 prose-base/medium", {
          skeleton: isPlaceholderData,
        })}
      >
        New User Sign-ups
      </Title>

      <Flex justify="space-between" align="center" gap={16}>
        <Title
          fz={60}
          order={1}
          className={clsx("text-primary-background-white", {
            skeleton: isPlaceholderData,
          })}
        >
          {data?.total_new_signups}
        </Title>

        <Center
          p={12}
          className={clsx(
            "border rounded-full h-fit border-white/50 bg-gradient-shadow",
            {
              skeleton: isPlaceholderData,
            }
          )}
        >
          <NewUserSignupIcon />
        </Center>
      </Flex>

      <Flex gap={8} align="center">
        <Center p="xs" c="accent" bg="white" fz={12} className="rounded-full">
          {isNegative(pass.number(view)) ? <ArrowDownIcon /> : <ArrowUpIcon />}
        </Center>

        <Flex
          columnGap={4}
          rowGap={0}
          wrap="wrap"
          className={clsx("prose-sm/medium text-primary-background-white", {
            skeleton: isPlaceholderData,
          })}
          align="center"
        >
          <Text fw={500} c="white">
            {round(abs(pass.number(view)), 2)}%
          </Text>
          <Text className="text-white/50">
            vs last {metric.slice(0, -2).toLocaleLowerCase()}
          </Text>
        </Flex>
      </Flex>
    </Stack>
  );
}
